import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '../lib/api.js'
import SafeHtml from '../components/ui/SafeHtml.jsx'

const DIFF_COLORS_DARK = {
  facile:    'bg-blue-500/20 text-blue-300',
  moyen:     'bg-amber-500/20 text-amber-300',
  difficile: 'bg-red-500/20 text-red-300',
}

export default function GenerateDeckPage({ navBase = '/host/decks' }) {
  const { deckId } = useParams()
  const navigate = useNavigate()
  const [phase, setPhase] = useState('generate-form')
  const [genForm, setGenForm] = useState({ description: '', nb_sous_themes: 3, nb_questions_par_sous_theme: 5 })
  const [preview, setPreview] = useState(null)
  const [importing, setImporting] = useState(false)
  const [expandedIndex, setExpandedIndex] = useState(null)
  const [genError, setGenError] = useState('')

  const setGen = (k, v) => setGenForm(f => ({ ...f, [k]: v }))

  async function handleGenerate(e) {
    e.preventDefault()
    setGenError('')
    setPhase('generate-loading')
    try {
      const result = await apiFetch(`/api/decks/${deckId}/generate`, {
        method: 'POST',
        body: JSON.stringify(genForm),
      })
      setPreview(result)
      setExpandedIndex(null)
      setPhase('generate-preview')
    } catch (e) {
      setGenError(e.message)
      setPhase('generate-preview')
    }
  }

  async function handleImport() {
    setImporting(true)
    setGenError('')
    try {
      await apiFetch(`/api/decks/${deckId}/generate/import`, {
        method: 'POST',
        body: JSON.stringify(preview),
      })
      navigate(`${navBase}/${deckId}/questions`)
    } catch (e) {
      setGenError(e.message)
    }
    setImporting(false)
  }

  const nbQuestions = preview?.questions?.length ?? 0
  const nbSousThemes = preview?.sous_themes?.length ?? 0

  function headerTitle() {
    if (phase === 'generate-form') return '🪄 Compléter avec l\'IA'
    if (phase === 'generate-loading') return 'Génération en cours…'
    return `Aperçu — ${nbSousThemes} sous-thème${nbSousThemes > 1 ? 's' : ''} · ${nbQuestions} question${nbQuestions > 1 ? 's' : ''}`
  }

  return (
    <div className="min-h-[100dvh] bg-gray-900 flex flex-col">
      <header className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-sm border-b border-white/10 px-4 py-3 flex items-center gap-3 shrink-0">
        <button
          onClick={() => navigate(`${navBase}/${deckId}/questions`)}
          className="text-white/70 hover:text-white transition p-1 rounded-lg hover:bg-white/10 shrink-0"
          aria-label="Retour"
          disabled={phase === 'generate-loading'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <img src="/android-chrome-192x192.png" alt="KartQuiz" className="w-7 h-7 rounded-lg shrink-0" />
        <span className="font-semibold text-white">{headerTitle()}</span>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-xl mx-auto px-4 py-8">

          {phase === 'generate-form' && (
            <form onSubmit={handleGenerate} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Description du sujet</label>
                <textarea
                  required
                  rows={4}
                  value={genForm.description}
                  onChange={e => setGen('description', e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="ex. Révision sur la Révolution française pour le bac de terminale"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Nombre de sous-thèmes</label>
                  <input
                    type="number" min={1} max={8}
                    value={genForm.nb_sous_themes}
                    onChange={e => setGen('nb_sous_themes', parseInt(e.target.value) || 3)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">Questions / sous-thème</label>
                  <input
                    type="number" min={1} max={30}
                    value={genForm.nb_questions_par_sous_theme}
                    onChange={e => setGen('nb_questions_par_sous_theme', parseInt(e.target.value) || 5)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-400/30 text-blue-300 rounded-lg px-4 py-3 text-sm">
                La génération peut prendre 15 à 30 secondes selon la longueur du contenu demandé.
              </div>

              <div className="flex gap-3 justify-end pt-2 pb-8">
                <button type="button" onClick={() => navigate(`${navBase}/${deckId}/questions`)} className="px-4 py-2 text-white/60 hover:text-white transition">
                  Annuler
                </button>
                <button type="submit" className="btn-primary px-6 py-2">
                  Générer
                </button>
              </div>
            </form>
          )}

          {phase === 'generate-loading' && (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <svg className="animate-spin h-10 w-10 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              <p className="text-lg font-medium text-white">Génération en cours…</p>
              <p className="text-sm text-white/50">Cela peut prendre 15 à 30 secondes</p>
            </div>
          )}

          {phase === 'generate-preview' && (
            <div className="space-y-4 pb-8">
              {genError ? (
                <>
                  <div className="bg-red-500/15 border border-red-500/30 text-red-300 rounded-lg px-4 py-3 text-sm">{genError}</div>
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => navigate(`${navBase}/${deckId}/questions`)} className="px-4 py-2 text-white/60 hover:text-white transition">
                      Annuler
                    </button>
                    <button
                      onClick={() => { setGenError(''); setPhase('generate-form') }}
                      className="btn-primary px-6 py-2"
                    >
                      Réessayer
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    {preview.sous_themes.map((st, stIdx) => {
                      const questionsTheme = preview.questions.filter(q => q.sous_theme_index === stIdx)
                      const isOpen = expandedIndex === stIdx
                      return (
                        <div key={stIdx} className="border border-white/10 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setExpandedIndex(isOpen ? null : stIdx)}
                            className="w-full flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 transition text-left"
                          >
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-semibold shrink-0">
                                {stIdx + 1}
                              </span>
                              <span className="font-medium text-white">{st.nom}</span>
                              <span className="text-xs text-white/40">
                                {questionsTheme.length} question{questionsTheme.length > 1 ? 's' : ''}
                              </span>
                            </div>
                            <svg
                              className={`w-4 h-4 text-white/40 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>

                          {isOpen && (
                            <div className="divide-y divide-white/10">
                              {questionsTheme.map((q, qIdx) => (
                                <div key={qIdx} className="px-4 py-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${DIFF_COLORS_DARK[q.difficulte] ?? 'bg-white/10 text-white/60'}`}>
                                      {q.difficulte}
                                    </span>
                                    <p className="text-sm font-medium text-white"><SafeHtml html={q.enonce} /></p>
                                  </div>
                                  <div className="grid grid-cols-2 gap-1">
                                    {q.reponses.map((r, i) => (
                                      <div
                                        key={i}
                                        className={`text-xs px-2 py-1 rounded ${
                                          q.bonne_reponse === i
                                            ? 'bg-green-500/20 text-green-300 font-medium'
                                            : 'bg-white/5 text-white/50'
                                        }`}
                                      >
                                        {String.fromCharCode(65 + i)}. {r}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  <div className="flex gap-3 justify-end pt-2">
                    <button onClick={() => navigate(`${navBase}/${deckId}/questions`)} className="px-4 py-2 text-white/60 hover:text-white transition">
                      Annuler
                    </button>
                    <button
                      onClick={handleImport}
                      disabled={importing}
                      className="btn-primary px-6 py-2 disabled:opacity-60"
                    >
                      {importing ? 'Import en cours…' : `Importer tout (${nbQuestions} questions)`}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </main>
    </div>
  )
}
